import React, { useEffect, useState } from "react";
import gsap from "gsap";
import { useLocation } from "react-router-dom";
import { ScrollTrigger, ScrollToPlugin, SplitText } from "gsap/src/all";

import Box from "@mui/material/Box";
import Prompt from "../../components/Prompt/Prompt";

import {
    TResponse,
    IMessagePageContent,
    IPromptRequest,
} from "../../services/interface/data";
import Proposals from "../../components/Proposals/Proposals";
import Navigation from "../../components/Navigation/Navigation";
import Image from "../../components/Image/Image";
import PromptEntry from "../../components/PromptEntry/PromptEntry";
import DataService from "../../services/DataService";
import useData from "../../hooks/useData";

import renderSection from "../../components/renderSection";

const Main = () => {
    const location = useLocation();
    const { response, setRequestMessage } = DataService.usePrompt(
        location.pathname
    );
    const { data, updateData } = useData();
    useState<TResponse<IMessagePageContent>>();

    const [promptUIState, setPromptUIState] = useState<
        "closed" | "open" | "scrollable"
    >("closed");

    /**
     * EVENT HANDLERS
     * ------------------------------------------------------------
     */

    /**
     * handleSubmitPrompt
     * @description Submits prompt text to DataService
     * @param {string} inputValue
     * @returns {void}
     */
    const handleSubmitPrompt = (inputValue: string) => {
        console.log("handleSubmitPrompt -> inputValue", inputValue);
        /**
         *  NOTE: If routing is added, this is probably the right condition
            put that in place. Make the route change on submit, and the 
            conditionals below for hiding the home placeholders shouldn't be
            necessary. 
            
            The new route should be made responsible for retrieving data, 
            first by checking for local storage, then by fetching from the
            API. This way, browser history will allow the user to move between
            pages/prompts.  
         */
        const request: IPromptRequest = {
            thread_id: null,
            message: inputValue,
        };

        setRequestMessage(request);
    };

    /**
     * EFFECT HANDLERS
     * ------------------------------------------------------------
     */

    useEffect(() => {
        gsap.defaults({ ease: "power1.inOut", duration: 0.66 });
        gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, SplitText);
    }, []);

    useEffect(() => {
        if (!response) {
            return;
        }
        updateData(response);
        console.log("useEffect:response -> updateData", response);
    }, [response, updateData]);

    const responseMessage = data?.response_msg;
    data && console.log("🚀 ~ Main ~ data:", data);

    return (
        <div data-name="Main" className={`promptUI--${promptUIState}`}>
            {!responseMessage && <Navigation />}

            {!responseMessage && <Image />}

            <Prompt
                data={data}
                promptUIState={promptUIState || "closed"}
                setPromptUIState={setPromptUIState}
                handleSubmitPrompt={handleSubmitPrompt}
            />
            {/* 
                NOTE: The element below is working as a "pin-spacer". The one that
                GSAP Scrolltrigger uses is added too late, and causes the position
                of other elements to "jump" when the prompt is closing. It should 
                never actually be seen by the user.

                TODO: move the height value to a variable, and use it in the
                Prompt component as well.
            */}
            {responseMessage && <Box height={"560px"} />}

            {data?.follow_up_prompts && !!data.follow_up_prompts.length && (
                <Proposals
                    data={data.follow_up_prompts}
                    handleSubmitPrompt={handleSubmitPrompt}
                />
            )}

            {!responseMessage && <Box height={"100vh"} bgcolor={"#ccc"} />}

            {data && data.page_content && (
                <>
                    {data.page_content.map((contentItem) =>
                        renderSection(contentItem)
                    )}
                    <PromptEntry
                        handleSubmitPrompt={() => {}}
                        variant="end-page"
                    />
                </>
            )}
        </div>
    );
};

export default Main;
